/**
 * Socket store - manages Socket.IO connection lifecycle.
 * Event handlers are registered in domain-specific modules under handlers/.
 */

import { create } from "zustand";
import { Platform } from "react-native";
import { io, Socket } from "socket.io-client";
import { nanoid } from "nanoid";
import type {
  ServerToClientEvents,
  ClientToServerEvents,
} from "@amzn/a4k-creator-studio-shared";
import { getVoiceSocketUrl } from "../utils/AppConfig";
import { useAudioStore } from "./audioStore";
import { useChatStore } from "./chatStore";
import { useFaceStore } from "./faceStore";
import { useNavigationStore } from "../navigation/navigationStore";
import { generateFullComic } from "../services/comicGenerator";
import { getClientUserId } from "../utils/getClientUserId";

import authService from "../services/authService";
import minervaService from "../services/minervaService";
import { trackMuseEvent, getAppSessionId, Muse } from "../services/metrics";
import { AppEvent } from "../services/appEvents";
import { registerConnectionHandlers, endSessionIfActive, stopAccessRetry } from "./handlers/connectionHandlers";
import { registerFullStateHandlers } from "./handlers/fullStateHandlers";
import { registerGalleryHandlers } from "./handlers/galleryHandlers";
import { registerVoiceStreamHandlers } from "./handlers/voiceStreamHandlers";
import { registerStoryHandlers } from "./handlers/storyHandlers";
import { registerComicHandlers } from "./handlers/comicHandlers";
import { registerESSHandlers } from "./handlers/essHandlers";
import { setImageSocket } from "../lib/imageLoader";
import { registerSharingHandlers } from "./handlers/sharingHandlers";
import { registerSessionInfoHandlers } from "./handlers/sessionInfoHandlers";
import { registerDebugHandlers } from "./handlers/debugHandlers";
import type { EntryGreeting } from "../audio/greetings";

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

interface SocketState {
  socket: TypedSocket | null;
  isConnected: boolean;
  /** True once the server has pushed the initial fullState after connect */
  fullStateReceived: boolean;
  sessionId: string;
  /** Lightweight session info received on fresh launch (before fullState) */
  hasExistingStory: boolean;
  storyTitle: string | null;
  sessionInfoReceived: boolean;
  /** True when waiting for story data (resume session or load project) */
  storyLoading: boolean;
  /** True once the user has entered an active session (any path into StoryBuilder). Used for reconnect logic. */
  sessionActive: boolean;
  /** Pending request to auto-start voice once the builder is ready (set by the
   *  entry paths, consumed by StoryBuilder). */
  autoStartVoice: boolean;
  /** Greeting to play when the auto-started voice session opens, if any. */
  entryGreeting: EntryGreeting | null;
  isVoiceActive: boolean;
  /** Voice paused for a transient reconnect (resumable), distinct from stopped. */
  voiceSuspended: boolean;
  transcriptText: string;
  /** True when another browser tab/instance took over this session */
  sessionTakenOver: boolean;
  /** Non-null when access is blocked (VPC_NOT_GRANTED | SETTINGS_DISABLED) */
  accessBlocked: string | null;

  initSocket: () => Promise<void>;
  cleanUpSocket: () => void;
  refreshAuth: () => Promise<void>;
  emitEvent: (event: string, data?: unknown) => void;
  startVoice: (opts?: { resume?: boolean }) => Promise<void>;
  stopVoice: () => void | Promise<void>;
  restartVoice: () => Promise<void>;
  /** Pause/resume voice across a transient reconnect without the chat-clear and
   *  metrics of a real stop/start. */
  suspendVoice: () => void;
  resumeVoice: () => Promise<void>;
  /** Mute/unmute: toggle mic without killing Bedrock stream (7s grace). */
  muteVoice: () => void;
  unmuteVoice: () => void;
  generateComic: () => void;
  sendTextMessage: (text: string) => void;
  // ESS projects
  listESSProjects: () => void;
  loadMoreESSProjects: (nextToken: string) => void;
  saveToESS: () => void;
  loadESSProject: (storyId: string, options?: { createIfMissing?: boolean; fallbackTitle?: string }) => void;
  deleteESSProject: (storyId: string) => void;
  previewESSProject: (storyId: string) => void;
  updateProjectTitle: (storyId: string, title: string) => void;
  // ESS comics — independent entities from projects.
  listESSComics: () => void;
  loadMoreESSComics: (nextToken: string) => void;
  previewESSComic: (comicId: string) => void;
  deleteESSComic: (comicId: string) => void;
  /** Manual retry of the one-shot comic-to-ESS save after a failure. */
  saveComicToCloud: (comicId: string) => void;
  // Sharing
  listSharedComics: () => void;
  loadMoreSharedComics: (nextToken: string) => void;
  getShareTargets: () => void;
  shareComic: (comicId: string, recipientId: string) => void;
  listSharesSent: (comicId: string) => void;
  previewSharedComic: (shareId: string) => void;
  /** Tell server to push fullState for the current Redis session. */
  resumeStory: () => void;
}

function getSessionIdHint(): string {
  if (Platform.OS === 'web') {
    return localStorage.getItem("story-builder-session-id") || nanoid();
  }
  return nanoid();
}

// Module-level lock: prevents concurrent initSocket calls (e.g. React StrictMode double-mount)
let socketInitPromise: Promise<void> | null = null;
let voiceLock: Promise<void> | null = null;

/** Returns true if a voice start/stop is currently in its cooldown window. */
export function isVoiceLocked(): boolean { return voiceLock !== null; }

/** Null the lock only if a later voice op hasn't already replaced it (a delayed
 *  release must not drop the newer op's lock). */
function releaseVoiceLock(mine: Promise<void>): void {
  if (voiceLock === mine) voiceLock = null;
}

/** Monotonic counter incremented on every new voice session (cold start).
 *  Used by streamComplete handler to ignore stale completions from old sessions. */
let voiceGeneration = 0;
export function getVoiceGeneration(): number { return voiceGeneration; }

/** When true, streamComplete auto-restart is completely suppressed.
 *  Set by loadESSProject (project switch in progress), cleared by startVoice.
 *  This is the primary defense — no timing assumptions, no races. */
let suppressAutoRestart = false;
export function isSuppressed(): boolean { return suppressAutoRestart; }
export function setSuppressed(value: boolean): void { suppressAutoRestart = value; }

/** Promise that resolves when the server sends streamComplete in response to our
 *  stopAudio. startVoice awaits this so it won't send sessionStart while the
 *  server is still tearing down the old stream. */
let pendingStopComplete: Promise<void> | null = null;
let resolvePendingStop: (() => void) | null = null;
let stopGateTimeout: ReturnType<typeof setTimeout> | null = null;

/** Called from loadESSProject before emitting stopAudio — arms the gate. */
function armStopGate(): void {
  // Clear any previous gate (rapid double-switch: second armStopGate must
  // not leave the first timeout dangling, which would prematurely clear gate 2).
  if (stopGateTimeout) { clearTimeout(stopGateTimeout); stopGateTimeout = null; }
  if (resolvePendingStop) { resolvePendingStop(); } // resolve stale waiters
  pendingStopComplete = new Promise((resolve) => { resolvePendingStop = resolve; });
  // Safety timeout: if streamComplete never arrives (disconnected, error), unblock after 2s
  stopGateTimeout = setTimeout(() => {
    console.warn("[socketStore] stopGate: TIMEOUT (2s, streamComplete never arrived)");
    clearStopGate();
  }, 2000);
  console.log("[socketStore] stopGate: armed (blocking startVoice until streamComplete)");
}

/** Called when streamComplete arrives — unblocks startVoice. */
export function clearStopGate(): void {
  if (stopGateTimeout) { clearTimeout(stopGateTimeout); stopGateTimeout = null; }
  if (resolvePendingStop) { resolvePendingStop(); }
  pendingStopComplete = null;
  resolvePendingStop = null;
}

/** Module-scoped reference to the pending streamComplete auto-restart timer. */
let autoRestartTimer: ReturnType<typeof setTimeout> | null = null;

/** Timer for the 7s mute grace window. If it fires, voice is fully stopped. */
let muteTimer: ReturnType<typeof setTimeout> | null = null;

/** Clear the mute timer if active. Called from streamComplete handler when the
 *  server kills the stream externally (e.g., renewal) while muted. Without this,
 *  unmuteVoice would try to resume audio on a dead stream. */
export function clearMuteTimer(): boolean {
  if (muteTimer) { clearTimeout(muteTimer); muteTimer = null; return true; }
  return false;
}

/** Set the auto-restart timer reference (called from voiceStreamHandlers).
 *  Clears any existing timer to prevent orphaned timers from firing. */
export function setAutoRestartTimer(timer: ReturnType<typeof setTimeout> | null): void {
  if (autoRestartTimer && timer !== null) { clearTimeout(autoRestartTimer); }
  autoRestartTimer = timer;
}

/** Shared local teardown: stops audio capture/playback and clears UI state.
 *  Used by both stopVoice and loadESSProject's inline stop to avoid diverging copies. */
function tearDownVoiceLocally(): void {
  const { audioProcessor, audioPlayer } = useAudioStore.getState();
  audioProcessor?.stop();
  audioPlayer?.stop();
  useAudioStore.getState().setStreaming(false);
  useFaceStore.getState().setVoiceActive(false);
}

/**
 * Cancel any pending streamComplete auto-restart timer. Called by startVoice/
 * stopVoice/loadESSProject to prevent a stale auto-restart from racing with
 * the new session lifecycle.
 */
export function cancelPendingAutoRestart(): void {
  if (autoRestartTimer) {
    clearTimeout(autoRestartTimer);
    autoRestartTimer = null;
  }
}

export const useSocketStore = create<SocketState>((set, get) => ({
  socket: null,
  isConnected: false,
  fullStateReceived: false,
  sessionId: getSessionIdHint(),
  hasExistingStory: false,
  storyTitle: null,
  sessionInfoReceived: false,
  storyLoading: false,
  sessionActive: false,
  autoStartVoice: false,
  entryGreeting: null as EntryGreeting | null,
  isVoiceActive: false,
  voiceSuspended: false,
  transcriptText: "",
  sessionTakenOver: false,
  accessBlocked: null,

  initSocket: async () => {
    if (get().socket) return;
    // If another initSocket call is already in flight, wait for it instead of creating a second socket
    if (socketInitPromise) return socketInitPromise;

    socketInitPromise = (async () => {
      try {
        const token = await authService.getToken() || "";

        // Re-check after async gap — the first call may have completed while we waited
        if (get().socket) return;

        const socketUrl = getVoiceSocketUrl();
        console.log(`[socketStore] Connecting to ${socketUrl}`);
        const sessionId = get().sessionId;
        const userId = await getClientUserId();

        const socket = io(socketUrl, {
          transports: ["polling", "websocket"],
          // auth callback is invoked on every connection attempt (including
          // reconnections) so the MAP token is always fresh.
          auth: async (cb) => {
            try {
              const freshToken = await authService.getToken();
              if (!freshToken) {
                console.error("[socketStore] AuthBridge returned no token — connection will fail auth");
                minervaService.recordAppEvent(AppEvent.AUTH_TOKEN_UNAVAILABLE);
              } else {
                console.log("[socketStore] Auth callback: token obtained");
              }
              cb({ token: freshToken || "", sessionId, userId, resumeState: get().sessionActive, appSessionId: getAppSessionId() });
            } catch (err) {
              console.error("[socketStore] Auth callback: getToken() rejected:", err instanceof Error ? err.message : "unknown error");
              minervaService.recordAppEvent(AppEvent.AUTH_TOKEN_REJECTED);
              cb({ token: "", sessionId, userId, resumeState: get().sessionActive, appSessionId: getAppSessionId() });
            }
          },
          extraHeaders: { "x-amz-access-token": token || "" },
          withCredentials: true,
          reconnection: true,
          reconnectionAttempts: 10,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 30000,
        });

        // Register handlers by domain
        registerConnectionHandlers(socket, set);
        registerFullStateHandlers(socket, set);
        registerGalleryHandlers(socket);
        registerVoiceStreamHandlers(socket, get, set);
        registerStoryHandlers(socket);
        registerComicHandlers(socket);
        registerESSHandlers(socket);
        registerSharingHandlers(socket);
        registerSessionInfoHandlers(socket, set);
        registerDebugHandlers(socket);

        setImageSocket(socket);
        set({ socket });
      } finally {
        socketInitPromise = null;
      }
    })();

    return socketInitPromise;
  },

  cleanUpSocket: () => {
    stopAccessRetry();
    endSessionIfActive();
    if (muteTimer) { clearTimeout(muteTimer); muteTimer = null; }
    const { socket } = get();
    if (socket) {
      socket.disconnect();
    }
    setImageSocket(null);
    set({ socket: null, isConnected: false });
  },

  refreshAuth: async () => {
    const { socket } = get();
    if (!socket) {
      console.warn("[socketStore] Socket already closed, skipping auth refresh");
      return;
    }
    try {
      const token = await authService.getToken();
      if (token) {
        socket.emit("authRefresh", { token });
      } else {
        console.warn("[socketStore] No token available for auth refresh");
      }
    } catch (err) {
      console.error("[socketStore] Failed to refresh auth token:", err);
      minervaService.recordAppEvent(AppEvent.AUTH_TOKEN_REFRESH_FAILED);
    }
  },

  emitEvent: (event, data) => {
    // Typed as `string` for convenience; socket.io-client types narrow this
    // aggressively against ClientToServerEvents so we cast here.
    (get().socket as any)?.emit(event, data);
  },

  startVoice: async (opts?: { resume?: boolean }) => {
    if (voiceLock) {
      console.log("[socketStore] startVoice: dropped (voiceLock held)");
      return;
    }
    // For cold starts: if voice is already active, another startVoice already
    // ran (e.g., component remount, subscription firing twice). Don't duplicate.
    if (!opts?.resume && get().isVoiceActive) {
      console.log("[socketStore] startVoice: dropped (already active, cold start would duplicate)");
      return;
    }
    // Wait for any in-progress stopAudio to complete on the server. Without
    // this, sessionStart races stopAudio processing and the server sees a
    // duplicate session ("Voice session storm").
    if (pendingStopComplete) {
      const gatePromise = pendingStopComplete;
      console.log("[socketStore] startVoice: awaiting stop gate...");
      const gateStart = Date.now();
      await Promise.race([gatePromise, new Promise<void>((r) => setTimeout(r, 3000))]);
      // Only force-clear if it's the SAME gate (not a newly armed one from a rapid second switch)
      if (pendingStopComplete === gatePromise) {
        console.warn("[socketStore] startVoice: gate still set after 3s hard timeout, force-clearing");
        clearStopGate();
      }
      console.log("[socketStore] startVoice: stop gate cleared after %dms", Date.now() - gateStart);
      // Re-check voiceLock after await — stopVoice may have run during the wait
      // (e.g., user navigated to gallery while we were waiting for teardown).
      if (voiceLock) {
        console.log("[socketStore] startVoice: dropped after gate (voiceLock acquired by stopVoice during wait)");
        return;
      }
    }
    cancelPendingAutoRestart(); // Cancel any pending streamComplete restart
    let unlock: () => void;
    const mine = voiceLock = new Promise<void>((r) => { unlock = () => r(); });

    try {
      if (!get().socket) await get().initSocket();
      const { socket, isConnected } = get();
      const { audioProcessor } = useAudioStore.getState();
      if (!socket || !isConnected || !audioProcessor) return;

      // Always (re)activate. On a resume (auto-restart, or resume-from-suspend)
      // preserve the chat/transcript and skip the start metric; on a cold start
      // (orb tap, gallery return, autoStartVoice) reset for a fresh session.
      set({ isVoiceActive: true, voiceSuspended: false });
      useAudioStore.getState().setStreaming(true);
      useFaceStore.getState().setVoiceActive(true);
      if (!opts?.resume) {
        voiceGeneration++;
        console.log("[socketStore] startVoice: cold start, voiceGeneration=%d", voiceGeneration);
        set({ transcriptText: "" });
        useChatStore.getState().clearMessages();
        minervaService.recordAppEvent(AppEvent.VOICE_SESSION_START);
        trackMuseEvent(Muse.VoiceStart, "success");
      }

      socket.emit("sessionStart");
      socket.emit("promptStart", {});
      socket.emit("audioStart");

      audioProcessor.start((audioData: string) => {
        socket.emit("audioInput", audioData);
      });

      // Clear suppress after 2s — long enough for any stale streamComplete from
      // the old session to arrive and be blocked, short enough to not interfere
      // with stream renewal (4 min). This is the ONLY place suppress is cleared,
      // so schedule it before the self-suspend below or a self-suspended start
      // could leave suppress stuck true and block a later session's renewal.
      const gen = voiceGeneration;
      setTimeout(() => { if (voiceGeneration === gen) suppressAutoRestart = false; }, 2000);

      // If the socket dropped while we were starting (a disconnect's suspendVoice
      // no-ops because isVoiceActive wasn't set yet), self-suspend so the mic is
      // stopped and the fullState resume on reconnect picks it back up — otherwise
      // the session is orphaned (active state, dead socket, no resume).
      if (!get().isConnected) {
        get().suspendVoice();
        return;
      }
    } finally {
      // Hold the lock briefly so rapid taps within the same frame are dropped.
      setTimeout(() => { unlock!(); releaseVoiceLock(mine); }, 300);
    }
  },

  stopVoice: async () => {
    if (voiceLock) await voiceLock;
    cancelPendingAutoRestart();
    // Clear mute grace timer — a real stop supersedes it.
    // If the timer was active, the stream is alive (muted but not killed yet),
    // so we must NOT early-return — we need to emit stopAudio to kill it.
    const wasMuted = muteTimer !== null;
    if (muteTimer) { clearTimeout(muteTimer); muteTimer = null; }
    if (!get().isVoiceActive && !get().voiceSuspended && !wasMuted) return;
    suppressAutoRestart = true; // Block stale streamComplete from restarting
    const { socket } = get();

    let unlock: () => void;
    const mine = voiceLock = new Promise<void>((r) => { unlock = () => r(); });

    try {
      tearDownVoiceLocally();
      // socket may already be gone (reconnect_failed, or promoting a suspend).
      if (socket) {
        armStopGate(); // Block next startVoice until server confirms stream closed
        console.log("[socketStore] stopVoice: armed stop gate, emitting stopAudio");
        socket.emit("stopAudio");
      }
      set({ isVoiceActive: false, voiceSuspended: false });
      useFaceStore.getState().setAgentThinking(false);
      trackMuseEvent(Muse.VoiceStop);
    } finally {
      setTimeout(() => { unlock!(); releaseVoiceLock(mine); }, 300);
    }
  },

  restartVoice: async () => {
    // If another voice operation is in progress, skip this restart entirely.
    // Unlike the old behavior (await + restart), dropping duplicates is safe
    // here because restartVoice is only called from auto-recovery paths
    // (voiceModeChanged, streamComplete) where another restart will follow.
    if (voiceLock) return;
    cancelPendingAutoRestart();
    let unlock: () => void;
    const mine = voiceLock = new Promise<void>((r) => { unlock = () => r(); });
    try {
      const { socket } = get();
      const { audioProcessor, audioPlayer } = useAudioStore.getState();
      if (socket) {
        audioProcessor?.stop();
        audioPlayer?.stop();
        socket.emit("stopAudio");
        set({ isVoiceActive: false });
        useAudioStore.getState().setStreaming(false);
        useFaceStore.getState().setVoiceActive(false);
        useFaceStore.getState().setAgentThinking(false);
      }
    } finally {
      // Release lock before calling startVoice, which acquires its own lock (300ms hold).
      unlock!();
      releaseVoiceLock(mine);
    }
    await get().startVoice();
  },

  suspendVoice: () => {
    // Handle both active voice and muted-but-stream-alive state
    if (!get().isVoiceActive && !muteTimer) return;
    const wasMutedWithStreamAlive = muteTimer !== null;
    if (muteTimer) { clearTimeout(muteTimer); muteTimer = null; }
    cancelPendingAutoRestart();
    tearDownVoiceLocally();
    // If muted (stream alive on server), emit stopAudio to clean it up.
    // Without this, the server stream leaks until Bedrock's 55s timeout.
    if (wasMutedWithStreamAlive && get().socket) {
      armStopGate();
      get().socket.emit("stopAudio");
    }
    set({ isVoiceActive: false, voiceSuspended: true });
    useFaceStore.getState().setAgentThinking(false);
  },

  resumeVoice: async () => {
    // Resume a suspended session via startVoice's resume path (fresh handshake,
    // chat/metrics preserved). If it can't complete (still disconnected, etc.)
    // startVoice leaves voiceSuspended set, so a later reconnect retries.
    if (!get().voiceSuspended) return;
    await get().startVoice({ resume: true });
  },

  /** Mute the mic (stop sending audio) without killing the Bedrock stream.
   *  If not unmuted within 7s, escalates to a full stopVoice. This avoids
   *  the HTTP/2 race condition from rapid stream kill/create on orb toggle. */
  muteVoice: () => {
    if (!get().isVoiceActive || voiceLock) return;
    const { audioProcessor, audioPlayer } = useAudioStore.getState();
    audioProcessor?.stop();
    audioPlayer?.stop();
    set({ isVoiceActive: false });
    useAudioStore.getState().setStreaming(false);
    useFaceStore.getState().setVoiceActive(false);
    useFaceStore.getState().setAgentThinking(false);
    trackMuseEvent(Muse.VoiceStop, "mute");
    // After 7s of silence, kill the stream for real to free Bedrock capacity.
    // Uses stopVoice() which handles all teardown. Do NOT null muteTimer here —
    // stopVoice checks it to know the stream is alive (wasMuted flag).
    muteTimer = setTimeout(() => {
      get().stopVoice();
    }, 7000);
  },

  /** Resume sending audio on the existing Bedrock stream (instant, no network call).
   *  If the 7s grace expired and the stream was killed, falls back to full startVoice. */
  unmuteVoice: () => {
    if (voiceLock) return;
    // Grace window still open — stream is alive, just resume audio
    if (muteTimer) {
      clearTimeout(muteTimer);
      muteTimer = null;
      const { audioProcessor } = useAudioStore.getState();
      const { socket } = get();
      if (audioProcessor && socket) {
        audioProcessor.start((audioData: string) => {
          socket.emit("audioInput", audioData);
        });
        set({ isVoiceActive: true });
        useAudioStore.getState().setStreaming(true);
        useFaceStore.getState().setVoiceActive(true);
        trackMuseEvent(Muse.VoiceStart, "unmute");
        return;
      }
    }
    // Grace expired or no audio processor — do a full start with resume
    // (preserves chat/transcript, doesn't clear conversation history)
    get().startVoice({ resume: true });
  },

  generateComic: () => {
    generateFullComic();
  },

  sendTextMessage: (text: string) => {
    const { socket } = get();
    if (!socket) return;
    useChatStore.getState().addMessage({ role: "user", source: "text", content: text });
    socket.emit("textInput", { content: text });
  },

  listESSProjects: () => {
    get().socket?.emit("listESSProjects", {});
  },

  loadMoreESSProjects: (nextToken: string) => {
    get().socket?.emit("listESSProjects", { nextToken });
  },

  saveToESS: () => {
    get().socket?.emit("saveToESS", {});
  },

  loadESSProject: (storyId: string, options?: { createIfMissing?: boolean; fallbackTitle?: string }) => {
    const socket = get().socket;
    if (!socket) return;
    // Stop the current voice session before switching projects. Without this,
    // the old Bedrock stream stays active and its streamComplete auto-restart
    // races with autoStartVoice from the new project — creating duplicate sessions.
    // Also fixes "Muse stays listening in gallery" (voice never stopped on nav).
    //
    // We inline the stop (not await stopVoice()) because loadESSProject is sync.
    // Setting isVoiceActive=false HERE ensures that when the old session's
    // streamComplete arrives later, wasVoiceActive will be false — preventing
    // the auto-restart. The key: we set isVoiceActive=false BEFORE startVoice
    // from autoStartVoice sets it back to true, because essProjectLoaded hasn't
    // arrived yet (storyLoading gates autoStartVoice).
    cancelPendingAutoRestart();
    // Clear mute timer — if the kid was muted, the stream is still alive and
    // needs to be killed as part of the project switch.
    const wasMutedWithStreamAlive = muteTimer !== null;
    if (muteTimer) { clearTimeout(muteTimer); muteTimer = null; }
    // Suppress ALL streamComplete auto-restarts until startVoice clears it.
    // This is the primary fix for the session storm — no timing assumptions.
    suppressAutoRestart = true;
    voiceGeneration++;
    console.log("[socketStore] loadESSProject: suppressAutoRestart=true, voiceGeneration=%d", voiceGeneration);
    if (get().isVoiceActive || wasMutedWithStreamAlive) {
      tearDownVoiceLocally();
      armStopGate(); // Block startVoice until server confirms stream closed
      socket.emit("stopAudio");
    }
    // Set isVoiceActive=false AND storyLoading=true atomically. storyLoading
    // gates autoStartVoice (won't fire until essProjectLoaded). isVoiceActive=false
    // ensures streamComplete's wasVoiceActive check returns false.
    set({ isVoiceActive: false, storyLoading: true, sessionActive: true, autoStartVoice: true, entryGreeting: "continue" });
    useNavigationStore.getState().resetToBuilder({ project: storyId });
    socket.emit("loadESSProject", { storyId, ...options });
  },

  deleteESSProject: (storyId: string) => {
    get().socket?.emit("deleteESSProject", { storyId });
  },

  previewESSProject: (storyId: string) => {
    get().socket?.emit("previewESSProject", { storyId });
  },

  updateProjectTitle: (storyId: string, title: string) => {
    get().socket?.emit("updateProjectTitle", { storyId, title });
  },

  listESSComics: () => {
    get().socket?.emit("listESSComics", {});
  },

  loadMoreESSComics: (nextToken: string) => {
    get().socket?.emit("listESSComics", { nextToken });
  },

  previewESSComic: (comicId: string) => {
    get().socket?.emit("previewESSComic", { comicId });
  },

  deleteESSComic: (comicId: string) => {
    get().socket?.emit("deleteESSComic", { comicId });
  },

  saveComicToCloud: (comicId: string) => {
    get().socket?.emit("saveComicToCloud", { comicId });
  },

  listSharedComics: () => {
    get().socket?.emit("listSharedComics", {});
  },
  loadMoreSharedComics: (nextToken: string) => {
    get().socket?.emit("listSharedComics", { nextToken });
  },
  getShareTargets: () => {
    get().socket?.emit("getShareTargets");
  },
  shareComic: (comicId: string, recipientId: string) => {
    get().socket?.emit("shareComic", { comicId, recipientId });
  },
  listSharesSent: (comicId: string) => {
    get().socket?.emit("listSharesSent", { comicId });
  },
  previewSharedComic: (shareId: string) => {
    get().socket?.emit("previewSharedComic", { shareId });
  },

  resumeStory: () => {
    const socket = get().socket;
    if (!socket) return;
    set({ storyLoading: true, sessionActive: true, autoStartVoice: true, entryGreeting: "continue" });
    useNavigationStore.getState().resetToBuilder();
    socket.emit("resumeStory");
  },
}));


// Expose for console testing
if (typeof window !== "undefined") {
  (window as any).useSocketStore = useSocketStore;
}
